import { useMemo, useState } from 'react';
import {
  useProfessorStatus, useProfessorSections, useGenerateExplanation, useCreateProfessorQuestion,
  useInviteCodes, useCreateInviteCode, useSetInviteCodeActive,
  type ProfessorSection, type InviteCode,
} from '../lib/api';
import { motion, AnimatePresence, springs, PageTransition, Pressable, Modal } from '../lib/motion';
import { haptic } from '../lib/haptics';

const CARD = 'bg-white rounded-2xl border border-slate-200/80 shadow-card';
const FIELD =
  'w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-slate-800 placeholder-slate-400 bg-white ' +
  'focus:outline-none focus:ring-2 focus:ring-squishy-teal/30 focus:border-squishy-teal';
const LABEL = 'text-xs font-semibold text-slate-600 uppercase tracking-wide';

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 6;
const MIN_QUESTION_CHARS = 10;
const JOIN_BASE = 'https://app.squishyschool.com/join';

type Tab = 'write' | 'codes';
type QType = 'mcq' | 'sata';

/** Server error codes mapped to something a professor can act on. */
const ERROR_COPY: Record<string, string> = {
  section_not_granted: 'You do not have teaching access to that class. Pick one of the classes an admin granted you.',
  not_professor: 'Your professor access is not active yet. An admin has to approve it first.',
  professor_not_verified: 'Your professor account is awaiting verification by an admin.',
  forbidden: 'You are not allowed to do that.',
  rate_limited: 'Too many requests right now. Please wait a moment and try again.',
};

function errText(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  if (!raw) return fallback;
  return ERROR_COPY[raw] ?? raw;
}

function sectionLabel(s: ProfessorSection): string {
  return [s.school_name, s.class_code, s.term ?? 'No term'].join(' / ');
}

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through to the legacy path */ }
  try {
    const el = document.createElement('textarea');
    el.value = text;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(el);
    return ok;
  } catch {
    return false;
  }
}

// ===== Page =================================================================

export function TeachPage() {
  const [tab, setTab] = useState<Tab>('write');
  const [classSectionId, setClassSectionId] = useState('');

  const { data: status, isLoading: statusLoading } = useProfessorStatus();
  const { data: sectionsData, isLoading: sectionsLoading, error: sectionsError } = useProfessorSections();

  const sections = sectionsData?.sections ?? [];
  // Fall back to the first granted class until the professor picks one.
  const activeId = classSectionId || sections[0]?.class_section_id || '';
  const selected = sections.find((s) => s.class_section_id === activeId) ?? null;

  const loading = statusLoading || sectionsLoading;

  return (
    <PageTransition className="max-w-3xl mx-auto space-y-5 pb-8">
      <div className="pt-1">
        <h1 className="font-display text-2xl font-semibold text-squishy-dark tracking-tight">Teach</h1>
        <p className="text-sm text-slate-500 mt-1">
          Write questions for your students and hand out invite codes for your classes.
        </p>
      </div>

      {/* Tabs */}
      <div className="inline-flex rounded-xl bg-slate-100 p-1">
        {([['write', 'Write a question'], ['codes', 'Invite codes']] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => { haptic.selection(); setTab(key); }}
            className={`relative px-4 py-2 min-h-[44px] text-sm font-semibold rounded-lg transition-colors ${
              tab === key ? 'text-squishy-dark' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab === key && (
              <motion.span layoutId="teach-tab-pill" className="absolute inset-0 bg-white rounded-lg shadow-sm" transition={springs.snappy} />
            )}
            <span className="relative">{label}</span>
          </button>
        ))}
      </div>

      {loading && (
        <section className={`${CARD} p-6`}>
          <div className="h-4 w-40 rounded bg-slate-100 animate-pulse" />
          <div className="mt-3 h-11 w-full rounded-xl bg-slate-100 animate-pulse" />
        </section>
      )}

      {!loading && sectionsError && (
        <section className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-700" role="alert">
          <span className="font-semibold">We could not load your classes.</span> {errText(sectionsError)}
        </section>
      )}

      {!loading && !sectionsError && sections.length === 0 && (
        <NoClassesState />
      )}

      {!loading && !sectionsError && sections.length > 0 && (
        <>
          <ClassPicker sections={sections} value={activeId} onChange={setClassSectionId} />

          {tab === 'write'
            ? <QuestionComposer classSectionId={activeId} selected={selected} />
            : <InviteCodesPanel classSectionId={activeId} selected={selected} />}
        </>
      )}
    </PageTransition>
  );
}

// ===== Empty state ==========================================================

function NoClassesState() {
  return (
    <section className={`${CARD} p-6 text-center`}>
      <div className="mx-auto w-12 h-12 rounded-2xl bg-teal-50 flex items-center justify-center">
        <svg className="w-6 h-6 text-squishy-tealDark" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5zm0 0v7m0-7l6.16-3.42A12 12 0 0112 21a12 12 0 01-6.16-10.42L12 14z" />
        </svg>
      </div>
      <h2 className="font-display text-lg font-semibold text-squishy-dark tracking-tight mt-3">No classes yet</h2>
      <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">
        An admin has not granted you any classes yet. Squishy School verifies every
        instructor directly, so reach out to your school admin with the class code
        and term you teach, then come back to this page.
      </p>
    </section>
  );
}

// ===== Class picker =========================================================

function ClassPicker({ sections, value, onChange }: {
  sections: ProfessorSection[];
  value: string;
  onChange: (id: string) => void;
}) {
  const selected = sections.find((s) => s.class_section_id === value);
  return (
    <section className={`${CARD} p-5 space-y-2`}>
      <label htmlFor="teach-class" className={LABEL}>Class</label>
      <select
        id="teach-class"
        value={value}
        onChange={(e) => { haptic.selection(); onChange(e.target.value); }}
        className={`${FIELD} min-h-[44px]`}
      >
        {sections.map((s) => (
          <option key={s.class_section_id} value={s.class_section_id}>{sectionLabel(s)}</option>
        ))}
      </select>
      <p className="text-xs text-slate-400">
        {selected?.class_name
          ? `${selected.class_name}. `
          : ''}
        You can only post to the classes and terms an admin granted you.
      </p>
    </section>
  );
}

// ===== Write a question =====================================================

/** Drop blank options and remap the correct indices onto the compacted list. */
function compactOptions(options: string[], correct: number[]) {
  const kept: string[] = [];
  const remap = new Map<number, number>();
  options.forEach((raw, i) => {
    const text = raw.trim();
    if (!text) return;
    remap.set(i, kept.length);
    kept.push(text);
  });
  return {
    options: kept,
    correct: correct.filter((i) => remap.has(i)).map((i) => remap.get(i) as number),
  };
}

function QuestionComposer({ classSectionId, selected }: {
  classSectionId: string;
  selected: ProfessorSection | null;
}) {
  const [type, setType] = useState<QType>('mcq');
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '', '', '']);
  const [correct, setCorrect] = useState<number[]>([]);
  const [topic, setTopic] = useState('');
  const [subtopic, setSubtopic] = useState('');
  const [difficulty, setDifficulty] = useState(3);
  const [explanation, setExplanation] = useState('');
  const [proExplanation, setProExplanation] = useState('');
  // What the AI last produced, so we can tell whether the professor edited it.
  const [aiDraft, setAiDraft] = useState<{ explanation: string; proExplanation: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const generate = useGenerateExplanation();
  const create = useCreateProfessorQuestion();

  const explanationSource: 'human' | 'ai' | 'ai_edited' = !aiDraft
    ? 'human'
    : (explanation === aiDraft.explanation && proExplanation === aiDraft.proExplanation) ? 'ai' : 'ai_edited';

  const filledOptions = options.filter((o) => o.trim().length > 0).length;
  const validCorrect = correct.filter((i) => (options[i] ?? '').trim().length > 0);
  const canGenerate = question.trim().length >= MIN_QUESTION_CHARS && filledOptions >= 2 && validCorrect.length > 0;

  const setOption = (index: number, text: string) => {
    setOptions((prev) => prev.map((o, i) => (i === index ? text : o)));
  };

  const addOption = () => {
    if (options.length >= MAX_OPTIONS) return;
    haptic.light();
    setOptions((prev) => [...prev, '']);
  };

  const removeOption = (index: number) => {
    if (options.length <= MIN_OPTIONS) return;
    haptic.light();
    setOptions((prev) => prev.filter((_, i) => i !== index));
    setCorrect((prev) => prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i)));
  };

  const toggleCorrect = (index: number) => {
    haptic.selection();
    if (type === 'mcq') {
      setCorrect([index]);
      return;
    }
    setCorrect((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index].sort((a, b) => a - b)));
  };

  const switchType = (next: QType) => {
    if (next === type) return;
    haptic.selection();
    setType(next);
    // Going back to one answer, keep only the first marked option.
    if (next === 'mcq') {
      setCorrect((prev) => (prev.length > 1 ? [prev[0] as number] : prev));
    }
  };

  const resetForm = () => {
    setType('mcq');
    setQuestion('');
    setOptions(['', '', '', '']);
    setCorrect([]);
    setTopic('');
    setSubtopic('');
    setDifficulty(3);
    setExplanation('');
    setProExplanation('');
    setAiDraft(null);
    setFormError(null);
    setSaved(false);
    create.reset();
    generate.reset();
  };

  const runGenerate = async () => {
    if (!canGenerate || generate.isPending) return;
    setFormError(null);
    const packed = compactOptions(options, correct);
    try {
      const contextBits = [
        selected ? `${selected.class_code}${selected.class_name ? ` (${selected.class_name})` : ''}` : '',
        topic.trim(),
        subtopic.trim(),
      ].filter(Boolean);
      const r = await generate.mutateAsync({
        type,
        question: question.trim(),
        options: packed.options,
        correct: type === 'mcq' ? (packed.correct[0] as number) : packed.correct,
        context: contextBits.length > 0 ? contextBits.join(', ') : undefined,
      });
      setExplanation(r.explanation ?? '');
      setProExplanation(r.proExplanation ?? '');
      setAiDraft({ explanation: r.explanation ?? '', proExplanation: r.proExplanation ?? '' });
      haptic.success();
    } catch (err) {
      haptic.error();
      // The mutation error is rendered below the button.
      console.error('[teach] explanation generation failed', err);
    }
  };

  const validate = (): string | null => {
    if (!classSectionId) return 'Pick a class first.';
    if (question.trim().length < MIN_QUESTION_CHARS) return `Write a question of at least ${MIN_QUESTION_CHARS} characters.`;
    if (options.length < MIN_OPTIONS) return 'Add at least two answer options.';
    if (options.some((o) => o.trim().length === 0)) return 'Fill in every answer option, or remove the empty ones.';
    if (correct.length === 0) return type === 'sata' ? 'Mark at least one correct answer.' : 'Mark the correct answer.';
    if (type === 'mcq' && correct.length !== 1) return 'Multiple choice questions need exactly one correct answer.';
    return null;
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      haptic.warning();
      setFormError(problem);
      return;
    }
    setFormError(null);
    const packed = compactOptions(options, correct);
    try {
      await create.mutateAsync({
        classSectionId,
        type,
        question: question.trim(),
        options: packed.options,
        correct: type === 'mcq' ? (packed.correct[0] as number) : packed.correct,
        explanation: explanation.trim() || undefined,
        proExplanation: proExplanation.trim() || undefined,
        explanationSource,
        difficulty,
        topic: topic.trim() || undefined,
        subtopic: subtopic.trim() || undefined,
      });
      haptic.success();
      setSaved(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      haptic.error();
      console.error('[teach] save question failed', err);
    }
  };

  if (saved) {
    return (
      <section className={`${CARD} p-6 text-center`}>
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={springs.bouncy}
          className="mx-auto w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center"
        >
          <svg className="w-7 h-7 text-emerald-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </motion.div>
        <h2 className="font-display text-lg font-semibold text-squishy-dark tracking-tight mt-3">Question posted</h2>
        <p className="text-sm text-slate-500 mt-1.5">
          Your students in {selected ? sectionLabel(selected) : 'this class'} can practice it now.
        </p>
        <Pressable
          haptic="medium"
          onClick={resetForm}
          className="mt-5 w-full min-h-[52px] bg-gradient-to-br from-squishy-teal to-squishy-tealDark text-white font-bold text-[15px] rounded-2xl shadow-pop"
        >
          Write another
        </Pressable>
      </section>
    );
  }

  return (
    <div className="space-y-4">
      {/* Type + question */}
      <section className={`${CARD} p-5 space-y-4`}>
        <div className="space-y-2">
          <span className={LABEL}>Question type</span>
          <div className="inline-flex rounded-xl bg-slate-100 p-1">
            {([['mcq', 'Multiple choice'], ['sata', 'Select all that apply']] as const).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => switchType(key)}
                className={`relative px-4 py-2 min-h-[44px] text-sm font-semibold rounded-lg transition-colors ${
                  type === key ? 'text-squishy-dark' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {type === key && (
                  <motion.span layoutId="teach-type-pill" className="absolute inset-0 bg-white rounded-lg shadow-sm" transition={springs.snappy} />
                )}
                <span className="relative">{label}</span>
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400">
            {type === 'mcq' ? 'One answer is correct.' : 'Students must pick every correct answer.'}
          </p>
        </div>

        <div className="space-y-1.5">
          <label htmlFor="teach-question" className={LABEL}>Question</label>
          <textarea
            id="teach-question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={4}
            className={`${FIELD} resize-y min-h-[110px] leading-relaxed`}
            placeholder="A 62 year old patient is admitted with shortness of breath. Which finding should the nurse report first?"
          />
          <p className="text-xs text-slate-400">{question.trim().length} characters. Minimum {MIN_QUESTION_CHARS}.</p>
        </div>
      </section>

      {/* Answer options */}
      <section className={`${CARD} p-5 space-y-3`}>
        <div className="flex items-center justify-between gap-3">
          <span className={LABEL}>Answer options</span>
          <span className="text-xs text-slate-400">
            {type === 'mcq' ? 'Tap the circle to mark the correct answer' : 'Tap the boxes to mark every correct answer'}
          </span>
        </div>

        <div className="space-y-2">
          <AnimatePresence initial={false}>
            {options.map((opt, i) => {
              const isCorrect = correct.includes(i);
              return (
                <motion.div
                  key={`option-${i}`}
                  layout
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={springs.soft}
                  className={`flex items-center gap-2 rounded-xl border px-2 py-1.5 transition-colors ${
                    isCorrect ? 'border-squishy-teal bg-teal-50/60' : 'border-slate-200 bg-white'
                  }`}
                >
                  <label className="shrink-0 w-11 h-11 flex items-center justify-center cursor-pointer">
                    <input
                      type={type === 'mcq' ? 'radio' : 'checkbox'}
                      name="teach-correct-answer"
                      checked={isCorrect}
                      onChange={() => toggleCorrect(i)}
                      aria-label={`Mark answer option ${i + 1} as correct`}
                      className={`w-5 h-5 border-slate-300 text-squishy-primary focus:ring-squishy-teal/40 ${
                        type === 'mcq' ? 'rounded-full' : 'rounded-md'
                      }`}
                    />
                  </label>

                  <input
                    value={opt}
                    onChange={(e) => setOption(i, e.target.value)}
                    aria-label={`Answer option ${i + 1}`}
                    className="flex-1 min-w-0 min-h-[44px] bg-transparent text-sm text-slate-800 placeholder-slate-400 focus:outline-none"
                    placeholder="Type an answer option"
                  />

                  <button
                    type="button"
                    onClick={() => removeOption(i)}
                    disabled={options.length <= MIN_OPTIONS}
                    aria-label={`Remove answer option ${i + 1}`}
                    className="shrink-0 w-11 h-11 flex items-center justify-center text-slate-300 hover:text-red-400 disabled:opacity-30 disabled:hover:text-slate-300 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                  </button>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        <Pressable
          type="button"
          onClick={addOption}
          disabled={options.length >= MAX_OPTIONS}
          className="w-full min-h-[44px] rounded-xl border-2 border-dashed border-slate-200 text-sm font-semibold text-squishy-tealDark hover:border-squishy-teal/60 hover:bg-teal-50/40 disabled:opacity-40 transition-colors"
        >
          {options.length >= MAX_OPTIONS ? 'Six options is the maximum' : '+ Add option'}
        </Pressable>
      </section>

      {/* Tags and difficulty */}
      <section className={`${CARD} p-5 space-y-3`}>
        <span className={LABEL}>Tags and difficulty</span>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor="teach-topic" className="text-xs text-slate-500">Topic (optional)</label>
            <input id="teach-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
                   className={`${FIELD} min-h-[44px]`} placeholder="Cardiac" />
          </div>
          <div className="space-y-1.5">
            <label htmlFor="teach-subtopic" className="text-xs text-slate-500">Subtopic (optional)</label>
            <input id="teach-subtopic" value={subtopic} onChange={(e) => setSubtopic(e.target.value)}
                   className={`${FIELD} min-h-[44px]`} placeholder="Heart failure" />
          </div>
        </div>
        <div className="space-y-1.5">
          <label htmlFor="teach-difficulty" className="text-xs text-slate-500">Difficulty</label>
          <select
            id="teach-difficulty"
            value={difficulty}
            onChange={(e) => setDifficulty(Number(e.target.value))}
            className={`${FIELD} min-h-[44px]`}
          >
            <option value={1}>1. Very easy</option>
            <option value={2}>2. Easy</option>
            <option value={3}>3. Medium</option>
            <option value={4}>4. Hard</option>
            <option value={5}>5. Very hard</option>
          </select>
        </div>
      </section>

      {/* Explanations */}
      <section className={`${CARD} p-5 space-y-4`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <span className={LABEL}>Explanation</span>
            <p className="text-xs text-slate-400 mt-1">Tell students why the answer is right. You can write it yourself or start from a draft.</p>
          </div>
        </div>

        <Pressable
          type="button"
          haptic="medium"
          onClick={runGenerate}
          disabled={!canGenerate || generate.isPending}
          className="w-full min-h-[52px] rounded-2xl bg-gradient-to-br from-squishy-navy via-squishy-navySoft to-squishy-purple text-white font-bold text-[15px] shadow-pop disabled:opacity-40 disabled:shadow-none transition-shadow"
        >
          <span className="flex items-center justify-center gap-2">
            {generate.isPending ? (
              <>
                <motion.span className="inline-flex" animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }} aria-hidden>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60" strokeLinecap="round" className="opacity-30" />
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="60" strokeDashoffset="45" strokeLinecap="round" />
                  </svg>
                </motion.span>
                Writing...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
                </svg>
                Write it for me
              </>
            )}
          </span>
        </Pressable>

        {!canGenerate && (
          <p className="text-xs text-slate-400">
            Add a question of at least {MIN_QUESTION_CHARS} characters, two or more answer options, and mark the correct answer to use this.
          </p>
        )}

        {generate.isError && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" role="alert">
            <span className="font-medium">We could not write it:</span> {errText(generate.error)}
          </div>
        )}

        <AnimatePresence initial={false}>
          {aiDraft && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={springs.snappy}
              className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-800 leading-relaxed"
            >
              <span className="font-semibold">Written by AI.</span>{' '}
              Please read both explanations and fix anything that is not accurate for your class before you post.
              {explanationSource === 'ai_edited' && <span className="block mt-1 font-medium">Saved as AI written with your edits.</span>}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="space-y-1.5">
          <label htmlFor="teach-explanation" className="text-xs text-slate-500">Explanation</label>
          <textarea
            id="teach-explanation"
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            rows={5}
            className={`${FIELD} resize-y min-h-[120px] leading-relaxed`}
            placeholder="Why the correct answer is correct, and why the others are not."
          />
        </div>

        <div className="space-y-1.5">
          <label htmlFor="teach-pro-explanation" className="text-xs text-slate-500">Pro explanation (deeper reasoning)</label>
          <textarea
            id="teach-pro-explanation"
            value={proExplanation}
            onChange={(e) => setProExplanation(e.target.value)}
            rows={5}
            className={`${FIELD} resize-y min-h-[120px] leading-relaxed`}
            placeholder="The extra depth: pathophysiology, clinical reasoning, test taking strategy."
          />
        </div>
      </section>

      {/* Errors + save */}
      <AnimatePresence initial={false}>
        {formError && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={springs.snappy}
            className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700"
            role="alert"
          >
            {formError}
          </motion.div>
        )}
      </AnimatePresence>

      {create.isError && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" role="alert">
          <span className="font-medium">Could not post the question:</span> {errText(create.error)}
        </div>
      )}

      <Pressable
        haptic="medium"
        onClick={save}
        disabled={create.isPending}
        className="w-full min-h-[54px] bg-gradient-to-br from-squishy-teal to-squishy-tealDark text-white font-bold text-[15px] rounded-2xl shadow-pop disabled:opacity-40 disabled:shadow-none transition-shadow"
      >
        {create.isPending ? 'Posting...' : 'Post to my class'}
      </Pressable>
    </div>
  );
}

// ===== Invite codes =========================================================

function InviteCodesPanel({ classSectionId, selected }: {
  classSectionId: string;
  selected: ProfessorSection | null;
}) {
  const { data, isLoading, error } = useInviteCodes();
  const createCode = useCreateInviteCode();
  const setActive = useSetInviteCodeActive();

  const [showCreate, setShowCreate] = useState(false);
  const [label, setLabel] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [justCreated, setJustCreated] = useState<string | null>(null);

  const codes = useMemo(() => {
    const all = data?.inviteCodes ?? [];
    // Codes for the class you are looking at come first, the rest stay visible below.
    return [...all].sort((a, b) => {
      const aMine = a.class_section_id === classSectionId ? 0 : 1;
      const bMine = b.class_section_id === classSectionId ? 0 : 1;
      return aMine - bMine;
    });
  }, [data, classSectionId]);

  const mineCount = codes.filter((c) => c.class_section_id === classSectionId).length;

  const submitCreate = async () => {
    if (!classSectionId || createCode.isPending) return;
    const parsedMax = maxUses.trim() ? Number(maxUses) : undefined;
    try {
      const r = await createCode.mutateAsync({
        classSectionId,
        label: label.trim() || undefined,
        maxUses: parsedMax && Number.isFinite(parsedMax) && parsedMax > 0 ? Math.floor(parsedMax) : undefined,
      });
      haptic.success();
      setJustCreated(r.inviteCode?.code ?? null);
      setLabel('');
      setMaxUses('');
      setShowCreate(false);
    } catch (err) {
      haptic.error();
      console.error('[teach] create invite code failed', err);
    }
  };

  return (
    <div className="space-y-4">
      <section className={`${CARD} p-5 space-y-3`}>
        <div>
          <h2 className="font-display text-base font-semibold text-squishy-dark tracking-tight">Invite codes</h2>
          <p className="text-sm text-slate-500 mt-1 leading-relaxed">
            Give a code to your students so they land in {selected ? sectionLabel(selected) : 'your class'} automatically.
          </p>
        </div>
        <Pressable
          haptic="medium"
          onClick={() => { createCode.reset(); setShowCreate(true); }}
          disabled={!classSectionId}
          className="w-full min-h-[52px] bg-gradient-to-br from-squishy-teal to-squishy-tealDark text-white font-bold text-[15px] rounded-2xl shadow-pop disabled:opacity-40"
        >
          Create code for this class
        </Pressable>
        {createCode.isError && !showCreate && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" role="alert">
            {errText(createCode.error)}
          </div>
        )}
      </section>

      {isLoading && (
        <section className={`${CARD} p-5 space-y-3`}>
          {[0, 1].map((i) => <div key={i} className="h-20 rounded-xl bg-slate-100 animate-pulse" />)}
        </section>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" role="alert">
          <span className="font-semibold">We could not load your codes.</span> {errText(error)}
        </div>
      )}

      {!isLoading && !error && codes.length === 0 && (
        <section className={`${CARD} p-6 text-center`}>
          <p className="text-sm text-slate-500">No invite codes yet. Create one and share it with your students.</p>
        </section>
      )}

      {!isLoading && codes.length > 0 && mineCount === 0 && (
        <p className="text-xs text-slate-400 px-1">
          No codes for the class you selected yet. Your other codes are listed below.
        </p>
      )}

      <div className="space-y-3">
        {codes.map((code) => (
          <InviteCodeCard
            key={code.id}
            code={code}
            highlight={code.code === justCreated}
            onToggleActive={(active) => {
              haptic.light();
              setActive.mutate({ id: code.id, active });
            }}
            toggling={setActive.isPending && setActive.variables?.id === code.id}
            toggleError={setActive.isError && setActive.variables?.id === code.id ? errText(setActive.error) : null}
          />
        ))}
      </div>

      {/* Create sheet */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} sheet="bottom">
        <div className="bg-white rounded-t-3xl w-full overflow-hidden shadow-pop">
          <div className="mx-auto mt-3 h-1 w-10 rounded-full bg-slate-200" aria-hidden />
          <div className="px-6 pt-4 pb-2">
            <h3 className="font-display text-xl font-semibold text-squishy-dark tracking-tight">New invite code</h3>
            <p className="text-xs text-slate-500 mt-1">{selected ? sectionLabel(selected) : 'Select a class first.'}</p>
          </div>
          <div className="px-6 py-4 space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="teach-code-label" className="text-xs text-slate-500">Label (optional)</label>
              <input
                id="teach-code-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className={`${FIELD} min-h-[44px]`}
                placeholder="Monday cohort"
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="teach-code-max" className="text-xs text-slate-500">Max uses (optional)</label>
              <input
                id="teach-code-max"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value.replace(/[^0-9]/g, ''))}
                inputMode="numeric"
                className={`${FIELD} min-h-[44px]`}
                placeholder="Leave blank for unlimited"
              />
            </div>
            {createCode.isError && (
              <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700" role="alert">
                {errText(createCode.error)}
              </div>
            )}
          </div>
          <div className="px-6 pb-6 flex gap-3">
            <Pressable
              onClick={() => setShowCreate(false)}
              className="flex-1 min-h-[52px] rounded-2xl border border-slate-200 text-sm font-semibold text-slate-600"
            >
              Cancel
            </Pressable>
            <Pressable
              haptic="medium"
              onClick={submitCreate}
              disabled={createCode.isPending || !classSectionId}
              className="flex-[2] min-h-[52px] rounded-2xl bg-squishy-primary text-white font-semibold text-[15px] disabled:opacity-40 shadow-soft"
            >
              {createCode.isPending ? 'Creating...' : 'Create code'}
            </Pressable>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function InviteCodeCard({ code, highlight, onToggleActive, toggling, toggleError }: {
  code: InviteCode;
  highlight: boolean;
  onToggleActive: (active: boolean) => void;
  toggling: boolean;
  toggleError: string | null;
}) {
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const joinLink = `${JOIN_BASE}/${code.code}`;

  const doCopy = async (what: 'code' | 'link') => {
    const ok = await copyToClipboard(what === 'code' ? code.code : joinLink);
    if (!ok) return;
    haptic.success();
    setCopied(what);
    window.setTimeout(() => setCopied(null), 2000);
  };

  const usesText = code.max_uses ? `${code.uses} of ${code.max_uses} uses` : `${code.uses} uses`;

  return (
    <motion.section
      layout
      initial={highlight ? { scale: 0.97, opacity: 0 } : false}
      animate={{ scale: 1, opacity: 1 }}
      transition={springs.soft}
      className={`${CARD} p-5 ${highlight ? 'ring-2 ring-squishy-teal' : ''} ${code.active ? '' : 'opacity-75'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-mono text-2xl font-bold tracking-[0.15em] text-squishy-dark break-all">{code.code}</div>
          <div className="text-xs text-slate-500 mt-1 truncate">
            {code.school_name} / {code.class_code} / {code.term ?? 'No term'}
          </div>
          {code.label && <div className="text-xs text-slate-400 mt-0.5 truncate">{code.label}</div>}
        </div>
        <span
          className={`shrink-0 text-[11px] font-semibold rounded-full px-2.5 py-1 ${
            code.active ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {code.active ? 'Active' : 'Paused'}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        <span>{usesText}</span>
        <span>{code.redemptions} joined</span>
        {code.expires_at && <span>Expires {new Date(code.expires_at).toLocaleDateString()}</span>}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Pressable
          type="button"
          onClick={() => doCopy('code')}
          className={`min-h-[44px] px-4 rounded-xl text-sm font-semibold transition-colors ${
            copied === 'code' ? 'bg-emerald-50 text-emerald-600' : 'bg-teal-50 text-squishy-tealDark active:bg-teal-100'
          }`}
        >
          {copied === 'code' ? 'Copied' : 'Copy code'}
        </Pressable>
        <Pressable
          type="button"
          onClick={() => doCopy('link')}
          className={`min-h-[44px] px-4 rounded-xl text-sm font-semibold transition-colors ${
            copied === 'link' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-600 active:bg-slate-200'
          }`}
        >
          {copied === 'link' ? 'Link copied' : 'Copy join link'}
        </Pressable>
        <Pressable
          type="button"
          onClick={() => onToggleActive(!code.active)}
          disabled={toggling}
          className="min-h-[44px] px-4 rounded-xl text-sm font-semibold border border-slate-200 text-slate-600 disabled:opacity-40 active:bg-slate-50 transition-colors"
        >
          {toggling ? 'Saving...' : code.active ? 'Pause' : 'Reactivate'}
        </Pressable>
      </div>

      <p className="mt-2 text-[11px] text-slate-400 break-all">{joinLink}</p>

      {toggleError && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700" role="alert">
          {toggleError}
        </div>
      )}
    </motion.section>
  );
}
